import { Box, Stack, Typography } from "@mui/material";

import type { PartialSong } from "@redux/player/types";
import { useEffect, useRef, useState } from "react";
import { Wheel } from "react-custom-roulette";
import { RouletteWinnerBox } from "./RouletteWinnerBox";
import { ReactComponent as RouletteWheelBorder } from "assets/roulette-wheel-border.svg";
import { ReactComponent as RouletteWheelBorderTsl } from "assets/roulette-wheel-border-tsl.svg";
import type { Playlist } from "src/@types/api-types";
import { colorsSelector } from "@redux/colorPalette/selectors";
import { useSelector } from "react-redux";
import { withBamLayoutSelector } from "@redux/user/selectors";

interface Props {
	onStopSpinning: () => void;
	launchRoulette: {
		players: string[];
		song: PartialSong;
		selectedPlayer: number;
		playlist: Playlist;
	} | null;
}

export function RouletteWheel({ onStopSpinning, launchRoulette }: Props) {
	const colors = useSelector(colorsSelector);
	const withBamLayout = useSelector(withBamLayoutSelector);
	const [mustStartSpinning, setMustStartSpinning] = useState(false);
	const [isWinnerView, setIsWinnerView] = useState(false);
	const imageBoxRef = useRef<HTMLDivElement | null>(null);

	const winnerTextContainer = useRef<HTMLDivElement | null>(null);
	const [containerWitdh, setContainerWidth] = useState<number>(30);

	const { players, selectedPlayer } = launchRoulette || {
		players: [],
		selectedPlayer: 0,
	};

	const generateSafeStyleIndexes = (
		length: number,
		styleCount: number,
	): number[] => {
		const indexes: number[] = [0];

		for (let i = 1; i < length; i++) {
			let next = (indexes[i - 1] + 1) % styleCount;

			if (i === length - 1 && next === indexes[0]) {
				next = (next + 1) % styleCount;
			}

			indexes.push(next);
		}
		return indexes;
	};

	const styles = [
		{
			textColor: "white",
			backgroundColor: withBamLayout ? "#E72E5C" : "#7622B2",
		},
		{
			textColor: "black",
			backgroundColor: withBamLayout ? "#FFC3E0" : "#D9A4FF",
		},
		{
			textColor: "white",
			backgroundColor: withBamLayout ? "#871233" : "#57008C",
		},
	];

	const styleIndexes = generateSafeStyleIndexes(players.length, styles.length);

	const data = players.map((player, index) => {
		return {
			option: player.charAt(0).toUpperCase() + player.slice(1),
			style: styles[styleIndexes[index]],
		};
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (winnerTextContainer.current) {
			setContainerWidth(winnerTextContainer.current.offsetWidth);
		}

		if (isWinnerView) {
			const timeOut = setTimeout(() => {
				onStopSpinning();
			}, 4000);
			return () => {
				clearTimeout(timeOut);
				setIsWinnerView(false);
				setMustStartSpinning(false);
			};
			// biome-ignore lint/style/noUselessElse: <explanation>
		} else {
			setMustStartSpinning(true);
			setIsWinnerView(false);
		}
	}, [isWinnerView, mustStartSpinning, launchRoulette]);

	return (
		<>
			<Box
				sx={{
					width: "100%",
					height: "100%",
					animation: "fadeIn 2s ease-in-out",
					"@keyframes fadeIn": {
						"0%": { opacity: 0 },
						"100%": { opacity: 1 },
					},
					display: isWinnerView ? "flex" : "none",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<RouletteWinnerBox display={isWinnerView ? "flex" : "none"}>
					<Stack
						ref={winnerTextContainer}
						width={"100%"}
						height={"100%"}
						alignItems="center"
						justifyContent={"center"}
						sx={{ backgroundColor: withBamLayout ? colors.gamesHighlightColor : "#7622B2" }}
					>
						<Typography
							variant="h6"
							fontWeight="bold"
							fontSize={containerWitdh / 20}
						>
							{data[selectedPlayer].option}
						</Typography>
					</Stack>
				</RouletteWinnerBox>
			</Box>
			<Stack
				position={"relative"}
				alignItems={"center"}
				justifyContent={"center"}
				width={"100%"}
				height={"100%"}
				mt={2}
				display={isWinnerView ? "none" : "block"}
				sx={{
					animation: "fadeIn 2s ease-in-out",
					"@keyframes fadeIn": {
						"0%": { opacity: 0 },
						"100%": { opacity: 1 },
					},
				}}
			>
				<Stack
					zIndex={0}
					height={"100%"}
					justifyContent={"center"}
					alignItems={"center"}
					sx={{
						"& > div:first-of-type": {
							width: "100% !important",
							height: "100% !important",
							maxHeight: imageBoxRef.current
								? imageBoxRef.current.offsetWidth * 0.9
								: "100%",
							maxWidth: imageBoxRef.current
								? imageBoxRef.current.offsetWidth * 0.9
								: "100%",
							aspectRatio: "1",
						},
					}}
				>
					<Wheel
						mustStartSpinning={mustStartSpinning}
						onStopSpinning={() => {
							setMustStartSpinning(false);
							setIsWinnerView(true);
						}}
						prizeNumber={selectedPlayer}
						data={data}
						outerBorderColor={withBamLayout ? "#871233" : "transparent"}
						innerRadius={10}
						innerBorderWidth={10}
						innerBorderColor="white"
						radiusLineWidth={0}
						fontFamily="Roboto"
						pointerProps={{
							style: {
								color: "white",
								backgroundColor: "transparent",
								transform: "rotate(15deg)",
								top: 0,
							},
							src: `${process.env.REACT_APP_ASSETS_BASE_URL}${withBamLayout ? "/roulette-winner-indicator.svg" : "/roulette-winner-indicator-tsl.svg"}`,
						}}
						spinDuration={0.7}
						fontSize={containerWitdh / 5}
					/>
				</Stack>

				<Box
					ref={imageBoxRef}
					sx={{
						position: "absolute",
						zIndex: 2,
						borderRadius: "100%",
						left: "50%",
						top: "50%",
						transform: "translate(-50%, -50%)",
						width: "50%",
						backgroundColor: withBamLayout ? colors.gamesHighlightColor : "#7622B2",
					}}
				>
					{withBamLayout ? <RouletteWheelBorder /> : <RouletteWheelBorderTsl />}
				</Box>
			</Stack>
		</>
	);
}
